import type { MedplumRequestOptions } from '@medplum/core'
import type { RefetchOnConflictOptions } from '@seen/core/client'
import type { ExtendedResource } from '@seen/core/ExtendedResource'
import type { JsonLike } from '@seen/core/json'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export type UsePatchMutationProxy<T extends ExtendedResource> = {
  instance: T
  mutateFn: (instance: T) => void
  patchOptions?: MedplumRequestOptions &
    RefetchOnConflictOptions & {
      skipVersionCheck?: boolean
    }
}

export interface UsePatchMutationProxyOptions<T extends ExtendedResource> {
  mutation?: Omit<
    UseMutationOptions<T, unknown, UsePatchMutationProxy<T>>,
    'mutationFn'
  >
}

// The `Type/id` reference a `usePatchMutation` call targets, read back from the
// mutation's recorded variables. The MutationCache types `variables` as
// `unknown`, so narrow structurally rather than casting. Used to ask "is a write
// of THIS resource still in flight?" across an unmount/remount: the Mutation
// outlives the observer that started it, so a PATCH fired by a torn-down
// editor's unmount flush is still visible to the next mount.
export function patchMutationTargetRef(
  variables: JsonLike | undefined
): string | undefined {
  if (typeof variables !== 'object' || variables === null) {
    return undefined
  }
  if (!('instance' in variables)) {
    return undefined
  }
  const { instance } = variables
  if (typeof instance !== 'object' || instance === null) {
    return undefined
  }
  // `in` walks the prototype chain, so a model class exposing fhirType/id as
  // prototype getters narrows here just like a plain object.
  if (!('fhirType' in instance) || !('id' in instance)) {
    return undefined
  }
  // Every mutation in the shared cache reaches this predicate, including
  // creates whose instance is unpersisted. `ExtendedResource`'s `id` getter
  // THROWS in that state, so bail before reading it.
  if ('isPersisted' in instance && instance.isPersisted === false) {
    return undefined
  }
  const { fhirType, id } = instance
  if (typeof fhirType !== 'string' || typeof id !== 'string') {
    return undefined
  }
  return `${fhirType}/${id}`
}

export function usePatchMutation<T extends ExtendedResource>(
  options?: UsePatchMutationProxyOptions<T> | null
): UseMutationResult<T, unknown, UsePatchMutationProxy<T>> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  const patchResourceInstance = medplum.patchResourceInstance
  const mutationFn = useCallback(
    async (args: UsePatchMutationProxy<T>) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: usePatchMutation',
          args.instance.fhirType
        )
      }
      const queryKey = cacheKeys.readExtended(
        args.instance.fhirType,
        args.instance.id,
        args.instance.constructor.name
      )
      const cachedInstance = queryClient.getQueryData<T>(queryKey)
      const result = await patchResourceInstance(
        cachedInstance ?? args.instance, // We want to use the cached instance since it may be more up to date than the instance passed in. This helps prevent version mismatch errors.
        args.mutateFn,
        args.patchOptions
      )
      return result
    },
    [
      queryClient, // Note queryClient needs to be passed in as a dep instead of just queryClient.getQueryData since queryClient.getQueryData relies on data in queryClient being up to date.
      patchResourceInstance,
      isReadOnly,
    ]
  )

  return useMutation({
    ...options?.mutation,
    onSuccess: (instance, variables, context) => {
      cacheKeys.invalidateMutation(queryClient, instance.fhirType, instance.id)
      queryClient.setQueryData(
        cacheKeys.read(instance.fhirType, instance.id),
        instance.toFhirResource()
      )
      const className = instance.constructor.name
      queryClient.setQueryData(
        cacheKeys.readExtended(instance.fhirType, instance.id, className),
        instance
      )
      options?.mutation?.onSuccess?.(instance, variables, context)
    },
    mutationFn,
  })
}
